"use client";

import { Button } from "@mui/material";
import { useStore } from "@nanostores/react";
import ErrorComponent from "common/components/errors/error-page";
import OpteraAppBar from "common/components/Nav/OpteraAppBar";
import { user as appUser, clearSession } from "common/stores/UserStore";
import { Endorsement, IUser, Permission } from "common/utils/UserUtils";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

const FullPageSpinner = dynamic(
	() => import("common/components/FullPageSpinner"),
	{
		ssr: false,
	}
);

export const publicPaths = ["/login"];
// NOTE: There are routes that may require generic authentication but no specific role authorizations
// these types of routes do not need to be listed in either publicRoutes or guardedRoutes

interface RouteConfig {
	path: string;
	enablingEndorsements: Endorsement[];
	enablingPermissions: Permission[];
}

/**
 * Configuration for authorization for routes within this app.
 * `path` in guarded routes will match in a startsWith fashion, so to secure a sub route different from a base route include both.
 * If a user has EITHER 1 or more enabling endorsement OR 1 or more enabling permission for a route they will be granted access.
 * At present this has to do with the fact that not all users have organizations, which behind the scenes are the entities that
 * impart endorsements to a user.
 * NOTE: roles on the token/cookie are aka permissions in auth0 - where roles are collections of permissions.
 */
const guardedRoutes: Array<RouteConfig> = [
	{
		path: "/survey/survey-management",
		enablingEndorsements: [],
		enablingPermissions: ["optera:admin"],
	},
	{
		path: "/survey/surveyProgressDashboard",
		enablingEndorsements: ["survey:surveyor"],
		enablingPermissions: ["survey:surveyor"],
	},
	{
		path: "/survey",
		enablingEndorsements: ["survey:surveyee", "survey:surveyor"],
		enablingPermissions: ["take:survey"],
	},
	{
		path: "/prototype",
		enablingEndorsements: [],
		enablingPermissions: ["optera:admin"],
	},
	{
		path: "/supplyChainManager",
		enablingEndorsements: ["supply_chain_insights:brand"],
		enablingPermissions: ["optera:admin"],
	},
	{
		path: "/supplyChain",
		enablingEndorsements: ["supplychain:brand", "supplychain:supplier"],
		enablingPermissions: [],
	},
	{
		path: "/esgInsightsUpload",
		enablingEndorsements: [],
		enablingPermissions: [],
	},
	{
		path: "/legacyOverview",
		enablingEndorsements: [
			"sam:brand",
			"supplychain:brand",
			"supplychain:supplier",
		],
		enablingPermissions: ["optera:admin"],
	},
];

/**
 * Use `guardedRoutes` configuration object to check if a given path is authorized for a given user
 */
export const checkUsersRouteAccess = (path: string, userInfo: IUser) => {
	// using find and startsWith like this means more specific routes (sub routes) should be listed before base routes in the guardedRoutes object
	const protectedRouteConfig = guardedRoutes.find((routeConfig) =>
		path.startsWith(routeConfig.path)
	);
	if (!protectedRouteConfig) {
		// This works off assumption that public or unguarded routes would not be listed in guardedRoutes config.
		// Depending on frequency of assessing access of public routes might be faster
		// to do publicPaths.includes(path) check before guardedRoutes.find(...)
		return true;
	}
	return (
		userInfo.endorsements?.some((endorsement) =>
			protectedRouteConfig.enablingEndorsements.includes(endorsement)
		) ||
		userInfo.roles?.some((role) =>
			protectedRouteConfig.enablingPermissions.includes(role)
		)
	);
};

/**
 * Intended to check if a user should be automatically routed off to flagstaff.
 * If the user has any endorsement or permission/role that could be used in this app, then they can stay.
 */
export const checkGeneralAccess = (userInfo: IUser) => {
	const allExpectedAppEndorsements = guardedRoutes.flatMap(
		(route) => route.enablingEndorsements
	);
	const allExpectedAppPermissions = guardedRoutes.flatMap(
		(route) => route.enablingPermissions
	);
	return (
		userInfo.endorsements?.some((endorsement) =>
			allExpectedAppEndorsements.includes(endorsement)
		) ||
		userInfo.roles?.some((role) => allExpectedAppPermissions.includes(role))
	);
};

export default function RouteGuard({ children }) {
	const $user = useStore(appUser);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true); // marks that we're on the client
	}, []);

	if (!mounted) {
		return <FullPageSpinner />;
	}

	// console.warn("1st things first before the spinner", $user);
	if (
		!$user ||
		!$user.username ||
		$user.username == "loading" ||
		typeof window === "undefined"
	) {
		return <FullPageSpinner />;
	}

	const path = window.location.pathname;

	// console.warn("getting past the 1st spinner", path, $user);
	// useful for debugging Next.js issues re persistent appContext.
	// console.log("route Guard check", user);

	// 401 response from fetch API isn't thrown as an actual error, HTTP call and response was technically successful.
	// 401 is also handled in MonarchClient so isn't thrown as axios typically would.
	// If not using swr, might look at response status code, but swr (which has other advantages) swallows that up,
	// so just looking at the content of response data will do
	if ($user.username == "error" || $user.username == "unauthenticated") {
		// public paths only need to be checked if auth fails.
		// this makes it so that even public paths will have user set in AppContext if the user was authenticated
		if (publicPaths.includes(path)) {
			return children;
		}

		return (
			<>
				<OpteraAppBar items={[]} />
				<ErrorComponent
					title="Authentication needed"
					message="You need to log in to access this page."
					buttons={
						<div className="flex-row gap-2">
							<Button
								variant="contained"
								onClick={() => clearSession()}
								href="/logout"
							>
								Go to Login
							</Button>
							<Button variant="outlined" href="/">
								Go Home
							</Button>
						</div>
					}
				/>
			</>
		);
	}

	// if we've previously authenticated, then only need to check for route authorization
	// this prevents unnecessary flashes on page transitions. NOTE, this assumes a persistent appContext between
	// page navigation so be sure to use Next/Link to get that
	if ($user && $user.organization_id != 0) {
		if (checkGeneralAccess($user) && checkUsersRouteAccess(path, $user)) {
			return children;
		} else {
			return (
				<>
					<OpteraAppBar items={[]} />
					<ErrorComponent
						title="Access Denied"
						message="You don’t have permission to view this page. If you believe this is an error, please contact support at support@opteraclimate.com."
						buttons={
							<div className="flex-row gap-2">
								<Button variant="contained" href="/">
									Go Home
								</Button>
								<Button variant="outlined" onClick={() => history.back()}>
									Go Back
								</Button>
							</div>
						}
					/>
				</>
			);
		}
	}

	// console.warn("getting to the spinner again");
	// renders a progress spinner in place of page during initial whoami check
	return <FullPageSpinner />;
}
